import { Link } from "react-router-dom";
import { SEO, serviceSchema, faqSchema, breadcrumbSchema } from "@/components/seo";
import { CTASection } from "@/components/common/CTASection";
import { FiArrowRight, FiCheck, FiTruck, FiTool, FiMonitor } from "@/lib/icons";
import { broker } from "@/config/broker.config";

const assetFinanceFaqs = [
  {
    question: "What is asset finance and how does it work in Brisbane?",
    answer:
      "Asset finance is a loan secured against the asset being purchased — vehicles, equipment, machinery, or technology. We arrange asset finance for businesses across Brisbane, Springfield, Ipswich, and Logan, comparing rates from over 70 lenders to find the right structure.",
  },
  {
    question: "What types of assets can I finance?",
    answer:
      "You can finance vehicles (cars, utes, trucks, fleet), equipment and machinery (construction, manufacturing, agricultural, medical), and technology (IT hardware, office fit-outs, point-of-sale systems).",
  },
  {
    question: "What is the difference between a chattel mortgage and a finance lease?",
    answer:
      "With a chattel mortgage you own the asset from day one and the lender takes a mortgage as security — good for claiming GST upfront and depreciation. With a finance lease the lender owns the asset and leases it to you, with options to buy out, extend, or return at term end.",
  },
  {
    question: "How long does asset finance approval take?",
    answer:
      "Many asset finance applications are approved within 24-48 hours. We handle the paperwork and lender liaison, so you can focus on running your business.",
  },
  {
    question: "Do I need a deposit for asset finance?",
    answer:
      "Many lenders offer 100% asset finance with no deposit, especially for established businesses. Some lenders may require a deposit depending on the asset type, age, and your trading history.",
  },
];

const assetTypes = [
  {
    icon: <FiTruck className="size-6" />,
    title: "Vehicles & Fleet",
    items: [
      "Cars, utes, and vans",
      "Trucks and heavy vehicles",
      "Fleet financing",
      "Electric vehicles",
    ],
  },
  {
    icon: <FiTool className="size-6" />,
    title: "Equipment & Machinery",
    items: [
      "Construction equipment",
      "Manufacturing machinery",
      "Agricultural equipment",
      "Medical equipment",
    ],
  },
  {
    icon: <FiMonitor className="size-6" />,
    title: "Technology & Office",
    items: [
      "IT hardware and software",
      "Office fit-outs",
      "Telecommunications",
      "Point of sale systems",
    ],
  },
];

const financeTypes = [
  {
    title: "Chattel Mortgage",
    description:
      "You own the asset from day one. The lender takes a mortgage over the asset as security. Good for claiming GST upfront and depreciation.",
    bestFor: "Businesses wanting ownership and tax benefits",
    gst: "Claim upfront",
  },
  {
    title: "Finance Lease",
    description:
      "The lender owns the asset and leases it to you. At the end, you can purchase for a residual, extend the lease, or return the asset.",
    bestFor: "Businesses wanting lower payments with flexibility",
    gst: "Claim on payments",
  },
  {
    title: "Operating Lease / Rental",
    description:
      "Off-balance sheet financing. Simply rent the asset with no ownership obligations. Good for assets that depreciate quickly.",
    bestFor: "Businesses wanting off-balance sheet treatment",
    gst: "Claim on payments",
  },
  {
    title: "Commercial Hire Purchase",
    description:
      "Similar to a chattel mortgage but structured as a hire arrangement. You take ownership after the final payment.",
    bestFor: "Businesses with regular income wanting fixed payments",
    gst: "Claim upfront",
  },
];

const benefits = [
  {
    title: "Preserve working capital",
    description: "Keep your cash for operations instead of tying it up in assets.",
  },
  {
    title: "Fixed repayments",
    description: "Budget with certainty - know exactly what you're paying each month.",
  },
  {
    title: "Tax benefits",
    description: "Interest and depreciation may be tax deductible. We'll help you choose the right structure.",
  },
  {
    title: "Fast approval",
    description: "Many asset finance applications can be approved within 24-48 hours.",
  },
];

const process = [
  {
    step: 1,
    title: "Tell us what you need",
    description: "Share details of the asset you want to finance and your business situation.",
  },
  {
    step: 2,
    title: "We find options",
    description: "We compare products from multiple lenders to find the best rate and structure.",
  },
  {
    step: 3,
    title: "Quick approval",
    description: "We submit your application and manage the approval process.",
  },
  {
    step: 4,
    title: "Settlement",
    description: "Funds are released to the vendor and you get your asset.",
  },
];

export function AssetFinancePage() {
  return (
    <>
      <SEO
        title="Asset Finance Brisbane, Ipswich & Springfield | Equipment & Vehicle Loans"
        description="Asset finance for businesses in Brisbane, Ipswich, Springfield & Logan. Chattel mortgage, finance lease & hire purchase from 70+ lenders. Fast 24-48hr approval."
        url="/business-loans/asset-finance"
        keywords={[
          "asset finance Brisbane",
          "asset finance Ipswich",
          "asset finance Springfield",
          "equipment finance Brisbane",
          "vehicle finance Brisbane",
          "chattel mortgage Brisbane",
          "asset finance loan",
          "asset finance options",
          "commercial hire purchase",
        ]}
        jsonLd={[
          serviceSchema({
            name: "Asset Finance",
            description:
              "Asset finance for vehicles, equipment, machinery and technology. Chattel mortgage, finance lease, operating lease and commercial hire purchase from 70+ lenders.",
            url: "/business-loans/asset-finance",
            serviceType: "Asset Finance",
          }),
          faqSchema(assetFinanceFaqs),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Business Loans", url: "/business-loans" },
            { name: "Asset Finance", url: "/business-loans/asset-finance" },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl">
            <Link
              to="/business-loans"
              className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6 transition-colors"
            >
              <FiArrowRight className="size-4 mr-2 rotate-180" />
              Business Loans
            </Link>
            <p className="section-label mb-4">Asset Finance</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
              Asset finance for Brisbane, Ipswich & Springfield businesses
            </h1>
            <p className="text-lg text-muted-foreground mb-8">
              Finance vehicles, equipment, machinery and technology without tying up
              working capital. We compare asset finance from over 70 lenders for
              businesses across Brisbane, Ipswich, Springfield and Logan — chattel
              mortgage, finance lease, operating lease and commercial hire purchase
              structures, with most approvals in 24-48 hours.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-medium hover:bg-primary/90 transition-colors"
              >
                Get a Quote
                <FiArrowRight className="size-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Asset Types */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">What We Finance</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              Assets we can help with
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {assetTypes.map((type) => (
              <div
                key={type.title}
                className="bg-white rounded-2xl p-6 border border-border/50"
              >
                <div className="p-3 bg-accent/20 rounded-xl inline-flex mb-4 text-primary">
                  {type.icon}
                </div>
                <h3 className="font-semibold text-lg mb-4">{type.title}</h3>
                <ul className="space-y-2">
                  {type.items.map((item) => (
                    <li
                      key={item}
                      className="flex items-center gap-2 text-sm text-muted-foreground"
                    >
                      <FiCheck className="size-4 text-primary flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Finance Types */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Options</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              Finance structures explained
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Different structures suit different situations. We'll help you choose
              based on your tax position, cash flow, and preferences.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {financeTypes.map((type) => (
              <div
                key={type.title}
                className="bg-white rounded-2xl p-6 border border-border/50"
              >
                <h3 className="font-semibold text-lg mb-2">{type.title}</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  {type.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs bg-muted px-2 py-1 rounded-lg">
                    Best for: {type.bestFor}
                  </span>
                  <span className="text-xs bg-accent/20 text-primary px-2 py-1 rounded-lg">
                    GST: {type.gst}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-primary/5 border border-primary/20 rounded-xl p-4">
            <p className="text-sm text-muted-foreground">
              <strong>Tax note:</strong> The tax treatment of asset finance depends
              on your business structure and circumstances. We recommend consulting
              your accountant to determine the best structure for your situation.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="section-label mb-4">Benefits</p>
              <h2 className="font-serif text-3xl md:text-4xl mb-6">
                Why finance your assets?
              </h2>
              <div className="space-y-4">
                {benefits.map((benefit) => (
                  <div key={benefit.title} className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FiCheck className="size-4 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{benefit.title}</h3>
                      <p className="text-sm text-muted-foreground">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-primary text-primary-foreground rounded-3xl p-8">
              <p className="section-label mb-4">
                The Process
              </p>
              <div className="space-y-6">
                {process.map((item) => (
                  <div key={item.step} className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-accent text-accent-foreground rounded-full flex items-center justify-center font-medium flex-shrink-0">
                      {item.step}
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{item.title}</h3>
                      <p className="text-sm text-primary-foreground/80">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">FAQ</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              Asset finance questions, answered
            </h2>
          </div>
          <div className="space-y-4">
            {assetFinanceFaqs.map((faq) => (
              <details
                key={faq.question}
                className="bg-white rounded-2xl p-6 border border-border/50 group"
              >
                <summary className="font-semibold cursor-pointer list-none flex items-center justify-between gap-4">
                  <span>{faq.question}</span>
                  <FiArrowRight className="size-4 shrink-0 transition-transform group-open:rotate-90" />
                </summary>
                <p className="mt-4 text-muted-foreground text-sm leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        label="Need to finance an asset?"
        title="Get a quick quote for your equipment or vehicle"
        description="Tell us what you need and we'll find the best finance option."
        primaryAction={{ label: "Get Quote", href: "/contact" }}
        secondaryAction={{
          label: "Call Us",
          href: `tel:${broker.phone.replace(/\s/g, "")}`,
        }}
      />
    </>
  );
}
